import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Column } from '../models/column';
import { ColumnFactory } from '../models/column-factory';
import {
  CatalogDictionary,
  CatalogMethod,
  CatalogsService,
  ExtensionOptions,
  GetColumnOptions,
} from './catalogs.service';
import { Data } from '../models/data';
import { mapOneColumnsLovsValues } from '../utils';
import { extendColumns } from '../extension';

/**
 * Available template types for columns/userid/ endpoints
 */
export enum UserCatalogTemplate {
  Map = '1',
  Table = '2',
  Clipboard = '3',
  CustomDetail = '4',
  SpaceSync = '5',
  Reports = '6',
  Company = '7',
  Lease = '8',
  Property = '9',
  AvailabilitiesGroupedByProperty = '10',
  AvailabilitiesGroupedSpace = '11',
  LeaseSpaceview = '12',
  AliasedCompanies = '13',
}

/**
 * User catalog related endpoints. fetch and update selected columns
 */
@Injectable()
export class UserCatalogsService extends CatalogsService {
  /**
   *
   * Returns persisted user columns selection by entity
   *
   * @param userId - the user id
   * @param entity - target entity to pull columns for
   * @param template - target column template
   *
   * @returns Observable of columns selected by user
   */
  public getUserColumns(
    userId: number,
    entity: string,
    templateID: UserCatalogTemplate,
    options: GetColumnOptions = {}
  ): Observable<Column[]> {
    const params = new HttpParams({ fromObject: { templateID } });
    let columnResponse$ = this.httpStreamService
      .httpGet(`/columns/${userId}/${entity}`, params)
      .pipe(map(columns => columns.map(ColumnFactory))) as Observable<Column[]>;

    // extend Column attributes
    if (options.extendColumns) {
      columnResponse$ = this.#connectColumnExtension$(columnResponse$, options.extendColumns);
    }

    if (options?.fetchAndMapLovs || options?.fetchAndMapLovsFromCache) {
      columnResponse$ = this.#connectLOVFetching$(columnResponse$, entity, options);
    }

    if (options?.mapUoms || options?.mapAllRelations) {
      columnResponse$ = this.#connectMapColumnRelations(columnResponse$, entity, options);
    }

    return columnResponse$;
  }

  #connectColumnExtension$ = (
    columnResponse$: Observable<Column[]>,
    extensionFw: ExtensionOptions<Column, Data>
  ): Observable<Column[]> =>
    columnResponse$.pipe(map(columns => extendColumns(columns, extensionFw.extension, extensionFw.context)));

  #connectLOVFetching$(
    columnResponse$: Observable<Column[]>,
    entity: string,
    options: GetColumnOptions
  ): Observable<Column[]> {
    if (options?.fetchAndMapLovsFromCache) {
      columnResponse$ = columnResponse$.pipe(
        switchMap(columns => this.#getColumnsListOfValuesFromCache$(columns, entity))
      );
    }
    return columnResponse$.pipe(switchMap(columns => this.#fetchAndMapLovs(columns, options)));
  }

  #getColumnsListOfValuesFromCache$<T extends Column = Column>(columns: T[], entity: string): Observable<T[]> {
    // request builder
    return this.httpStreamService
      .loadDataFor(`/lookup/wrapper/${entity}`)
      .pipe(map((lovMap: Record<string, Data[]>) => columns.map(mapOneColumnsLovsValues(lovMap))));
  }

  #fetchAndMapLovs = (columns: Column[], options: GetColumnOptions): Observable<Column[]> => {
    const lookupRequests$ = this.makeCatalogLovRequests$(columns, options.tokenReplacer);

    if (Object.keys(lookupRequests$).length === 0) {
      // return columns as if no more LOVs are required to fetch
      return of(columns);
    }
    // fetch remaining lovs that were not set by /wrapper (cache)
    return forkJoin(lookupRequests$).pipe(map(lovMap => columns.map(mapOneColumnsLovsValues(lovMap))));
  };

  #connectMapColumnRelations = (columns$: Observable<Column[]>, entity: string, options: GetColumnOptions) => {
    const allColumnsOpts = { ...options, mapUoms: false, mapAllRelations: false, fetchAndMapLovsFromCache: true };
    const allColumnsMap$ = this.getColumns(entity, CatalogMethod.Default, allColumnsOpts).pipe(
      map(this.makeColumnsDictionaryMap)
    );

    return forkJoin([columns$, allColumnsMap$]).pipe(
      map(([columns, catalogDictionary]) =>
        this.#doConnectColumnRelations(columns, entity, { ...options, catalogDictionary })
      )
    );
  };

  #doConnectColumnRelations(
    columns: Column[],
    entity: string,
    options: GetColumnOptions & { catalogDictionary: CatalogDictionary<Column> }
  ): Column[] {
    return this.moveUomColumnsToValueColumns(columns, entity, options);
  }

  /**
   * Updates user columns selection for given entity & template
   *
   * @param userId - user id
   * @param entity - target entity
   * @param template - target template
   * @param columnIds - selected column ids to save to target entity's template
   *
   * @returns Observable of update response
   */
  public putMoveColumns(userId: number, entity: string, templateID: UserCatalogTemplate, columnIds: number[]) {
    const params = new HttpParams({ fromObject: { templateID } });
    return this.httpStreamService.httpPut(`/columns/${userId}/${entity}`, columnIds.filter(Boolean), { params });
  }
}
